import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { CouponStatus } from 'src/app/shared/models/constants/coupon-status';
import { CouponLabels } from 'src/app/shared/models/coupon-labels/coupon-labels';
import { timeSpan } from 'src/app/utils/date';

@Component({
  selector: 'app-coupon-card',
  templateUrl: './coupon-card.component.html',
  styleUrls: ['./coupon-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Coupon card
 */
export class CouponCardComponent implements OnInit, AfterContentChecked {
  @Input() businessName!: string;
  @Input() equipmentName?: string;
  @Input() rewardName!: string;
  @Input() rewardValue!: number;
  @Input() couponCode!: string;
  @Input() expirationDate!: string;
  @Input() couponLabels = new CouponLabels();
  @Input() couponStatus?: CouponStatus;
  @Input() usedDate!: string;
  @Input() activityType?: 'coupon' | 'warranty';
  @Input() countryName?: string;
  @Input() equipmentSerial?: string;
  convertedExpirationDate?: any;
  convertedUsedDate?: any;
  dateFormat?: string;
  readonly redeemedText = 'redeemed-on';
  readonly canceledText = 'canceled-on';
  readonly noteSavedText = 'note-succesfully-saved';
  readonly activeText = 'coupon-expires-on';
  MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;
  language = '';
  /**
   * @constructor
   */
  constructor(
    public translationService: TranslationService,
    private cd: ChangeDetectorRef
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.convertedExpirationDate = new Date(this.expirationDate);
    this.translationService.translateObject(this.couponLabels, {
      couponValidCountry: {
        countryName: this.countryName,
      },
    });
    this.expirationDate = this.translationService.localizeDate(
      this.expirationDate
    );

    this.convertedUsedDate = new Date(this.usedDate);
    this.language = this.translationService.getActiveLang();
  }

  /**
   * Whether this activity is close to expiration
   */
  get isCloseToExpiration() {
    return (
      this.convertedExpirationDate &&
      !this.isExpired &&
      this.activityType === 'warranty' &&
      this.calculateDaysToExpiration() <= 90
    );
  }

  /**
   * Check card footer status
   */
  get statusCheck() {
    if (this.couponStatus === CouponStatus.REDEEMED) {
      return [this.redeemedText, this.convertedUsedDate];
    } else if (this.couponStatus === CouponStatus.CANCELED) {
      return [this.canceledText, this.convertedUsedDate];
    } else if (this.couponStatus === CouponStatus.ACTIVE) {
      return [this.activeText, this.convertedExpirationDate];
    } else if (!this.isCloseToExpiration) {
      return [this.expirationText, this.convertedExpirationDate];
    } else {
      return [this.expiringWithin, ''];
    }
  }

  /**
   * condition for red
   */
  get isCanceled() {
    return this.couponStatus === CouponStatus.CANCELED;
  }

  /**
   * condition for dark blue
   */
  get isRedeemed() {
    return this.couponStatus === CouponStatus.REDEEMED;
  }

  /**
   * condition for yellow
   */
  get isExpiring() {
    return (
      this.isCloseToExpiration &&
      this.couponStatus !== CouponStatus.REDEEMED &&
      this.couponStatus !== CouponStatus.CANCELED
    );
  }

  /**
   * Whether this activity has an expired date
   */
  get isExpired() {
    if (this.activityType === 'coupon') {
      return this.couponStatus === CouponStatus.EXPIRED;
    } else {
      return (
        this.convertedExpirationDate &&
        timeSpan(this.convertedExpirationDate) > 0
      );
    }
  }

  /**
   * Expiration text depends on activity type and expiration date
   */
  get expirationText() {
    if (this.isExpired) {
      return 'expired-on';
    } else if (this.isCloseToExpiration) {
      return 'expiring-on';
    } else {
      // expiration ok
      return 'active-warranty-expires-on';
    }
  }

  /**
   * Expiring within text
   */
  get expiringWithin() {
    if (this.calculateDaysToExpiration() === 1) {
      return 'expires-tomorrow';
    } else if (this.calculateDaysToExpiration() === 0) {
      return 'expires-today';
    } else {
      return 'expiring-within-days';
    }
  }

  /**
   * Whether this activity is far from expiration
   */
  get isActive() {
    return this.couponStatus === CouponStatus.ACTIVE;
  }

  /**
   * Calculate how many days lasts to expiration date
   * @return {number}
   */
  calculateDaysToExpiration() {
    const startDate = new Date();
    const expiresDate = this.convertedExpirationDate;

    startDate.setHours(0, 0, 0);
    expiresDate.setHours(23, 59, 59);

    return Math.floor(
      (Date.UTC(
        this.convertedExpirationDate.getFullYear(),
        this.convertedExpirationDate.getMonth(),
        this.convertedExpirationDate.getDate()
      ) -
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate()
        )) /
        this.MILLISECONDS_IN_DAY
    );
  }

  /**
   * Run  detection changes
   */
  ngAfterContentChecked(): void {
    this.cd.detectChanges();
  }
}
